/* ==========================================================
   locations-finder.js
   Depends on: window.GBClinics (set by locations-finder.liquid)
   Map: Leaflet (loaded via CDN in section, window.L)
   Geocoding: postcodes.io (free UK API, no key)
   ========================================================== */

(function () {
  'use strict';

  /* ── WAIT FOR LEAFLET ── */
  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ── CONSTANTS ── */
  var PAGE_SIZE = 8;

  /* ── STATE ── */
  var userLat = null;
  var userLng = null;
  var sortedClinics = [];
  var currentFilter = 'blood';
  var visibleCount = PAGE_SIZE;
  var currentLabel = '';
  var leafletMap = null;
  var markers = {};  // id → L.marker

  /* ── HAVERSINE DISTANCE (miles) ── */
  function calcDistance(lat1, lng1, lat2, lng2) {
    var R = 3958.8;
    var dLat = (lat2 - lat1) * Math.PI / 180;
    var dLng = (lng2 - lng1) * Math.PI / 180;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
          + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180)
          * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /* ── TODAY'S HOURS ── */
  function getTodayHours(hours) {
    var days     = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    var dayShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var today    = new Date().getDay();
    var parsed;

    try {
      parsed = typeof hours === 'string' ? JSON.parse(hours) : hours;
    } catch (e) {
      return { isOpen: false, text: 'Hours unavailable' };
    }

    if (!parsed || typeof parsed !== 'object' || Object.keys(parsed).length === 0) {
      return { isOpen: null, text: 'Hours not available' };
    }

    // Lookup helper: match a day key case-insensitively against the parsed object
    function findDay(dayIndex) {
      var fullName  = days[dayIndex];
      var shortName = fullName.substring(0, 3);
      var keys = Object.keys(parsed);
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i].toLowerCase();
        if (k === fullName || k === shortName || k.indexOf(shortName) === 0) {
          return parsed[keys[i]];
        }
      }
      return null;
    }

    var fmt = function (t) {
      var parts = String(t).split(':');
      var hh = parseInt(parts[0], 10);
      var mm = parseInt(parts[1] || '0', 10);
      var suffix = hh >= 12 ? 'pm' : 'am';
      var h12 = hh > 12 ? hh - 12 : hh === 0 ? 12 : hh;
      return mm === 0 ? h12 + suffix : h12 + ':' + String(mm).padStart(2, '0') + suffix;
    };

    var todayData = findDay(today);

    // Today is open
    if (todayData && todayData.open && todayData.close) {
      var now = new Date();
      var cur   = now.getHours() * 100 + now.getMinutes();
      var open  = parseInt(String(todayData.open).replace(':', ''), 10);
      var close = parseInt(String(todayData.close).replace(':', ''), 10);
      var isCurrentlyOpen = cur >= open && cur < close;

      return {
        isOpen: isCurrentlyOpen,
        text: (isCurrentlyOpen ? 'Open today: ' : 'Today: ') + fmt(todayData.open) + ' – ' + fmt(todayData.close)
      };
    }

    // Today is closed — find next open day
    for (var offset = 1; offset <= 7; offset++) {
      var nextIdx = (today + offset) % 7;
      var nextData = findDay(nextIdx);
      if (nextData && nextData.open && nextData.close) {
        var label = (offset === 1)
          ? 'Open tomorrow: '
          : 'Open ' + dayShort[nextIdx] + ': ';
        return {
          isOpen: false,
          text: label + fmt(nextData.open) + ' – ' + fmt(nextData.close)
        };
      }
    }

    // No days open in the whole week
    return { isOpen: null, text: 'By appointment only' };
  }

  /* ── SERVICE TAGS ── */
  function getServiceTags(testType) {
    if (!testType) return [];
    var t = testType.toLowerCase();
    var tags = [];
    if (t.indexOf('venous') !== -1 || t.indexOf('blood') !== -1) tags.push('blood');
    if (t.indexOf('ear') !== -1 || t.indexOf('microsuction') !== -1) tags.push('ear');
    if (t.indexOf('mri') !== -1) tags.push('mri');
    return tags;
  }

  var svcLabel = { blood: '🩸 Blood Tests', ear: '👂 Ear Wax Removal', mri: '🦲 MRI Scan' };
  var svcClass = { blood: 'lf-svc--blood', ear: 'lf-svc--ear', mri: 'lf-svc--mri' };

  function cleanTitle(title) {
    return String(title || '').replace(/^Location\s+/i, '');
  }

  /* ── RENDER CARD ── */
  function renderCard(clinic) {
    var h = getTodayHours(clinic.hours);
    var dist = typeof clinic._dist === 'number'
      ? (clinic._dist < 0.1 ? 'Nearby' : clinic._dist.toFixed(1) + ' mi')
      : '–';

    var tags = getServiceTags(clinic.testType);
    var svcsHTML = tags.map(function (t) {
      return '<span class="lf-svc ' + svcClass[t] + '">' + svcLabel[t] + '</span>';
    }).join('');

    var hoursClass = h.isOpen === true ? 'lf-card__hours--open'
                  : h.isOpen === false ? 'lf-card__hours--closed'
                  : 'lf-card__hours--unknown';

    var el = document.createElement('li');
    el.className = 'lf-card';
    el.setAttribute('role', 'listitem');
    el.dataset.id = clinic.id;
    el.innerHTML =
      '<div class="lf-card__header">' +
        '<h3 class="lf-card__name">' + escHtml(cleanTitle(clinic.title)) + '</h3>' +
        '<span class="lf-card__dist">' + dist + '</span>' +
      '</div>' +
      '<p class="lf-card__address">' + escHtml(clinic.address || '') + '</p>' +
      '<div class="lf-card__hours ' + hoursClass + '">' +
        '<span class="lf-card__hours-dot"></span>' +
        '<span class="lf-card__hours-text">' + escHtml(h.text) + '</span>' +
      '</div>' +
      '<div class="lf-card__services">' + svcsHTML + '</div>' +
      '<div class="lf-card__actions">' +
        '<a href="' + clinic.url + '" class="lf-btn lf-btn--book">Book Now →</a>' +
        '<a href="https://maps.google.com/?q=' + encodeURIComponent(clinic.address || '') + '"' +
           ' class="lf-btn lf-btn--dir" target="_blank" rel="noopener">📍 Directions</a>' +
      '</div>';

    el.addEventListener('mouseenter', function () { highlightPin(clinic.id, true); });
    el.addEventListener('mouseleave', function () { highlightPin(clinic.id, false); });
    el.addEventListener('click', function (e) {
      if (e.target.tagName === 'A') return;
      panToClinic(clinic);
    });

    return el;
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── FILTER + RENDER ── */
  function filteredClinics() {
    if (currentFilter === 'all') return sortedClinics;
    return sortedClinics.filter(function (c) {
      return getServiceTags(c.testType).indexOf(currentFilter) !== -1;
    });
  }

  function renderResults() {
    var listEl     = document.getElementById('lf-list');
    var countEl    = document.getElementById('lf-count');
    var moreWrap   = document.getElementById('lf-show-more');
    var moreBtn    = document.getElementById('lf-more-btn');

    listEl.innerHTML = '';

    var pool  = filteredClinics();
    var slice = pool.slice(0, visibleCount);

    if (pool.length === 0) {
      listEl.innerHTML =
        '<div class="lf-no-results">' +
          '<strong>No clinics found</strong>' +
          'Try a different postcode or remove a service filter.' +
        '</div>';
      moreWrap.style.display = 'none';
      countEl.textContent = 'No results';
      updateMapMarkers([]);
      return;
    }

    slice.forEach(function (c) { listEl.appendChild(renderCard(c)); });

    var remaining = pool.length - visibleCount;
    if (remaining > 0) {
      moreWrap.style.display = 'block';
      moreBtn.textContent = 'Show ' + Math.min(remaining, PAGE_SIZE) + ' more clinics';
    } else {
      moreWrap.style.display = 'none';
    }

    var near = currentLabel ? ' near ' + currentLabel : '';
    countEl.textContent =
      'Showing ' + slice.length + ' of ' + pool.length + ' clinics' + near;

    // Map always shows all filtered clinics, not just the current page
    updateMapMarkers(pool);
  }

  /* ── MAP ── */
  function makeIcon(highlighted) {
    var fill = highlighted ? '#22555B' : '#1B4348';
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">' +
        '<path d="M14 0C6.27 0 0 6.27 0 14c0 9.58 14 22 14 22S28 23.58 28 14C28 6.27 21.73 0 14 0z"' +
             ' fill="' + fill + '"/>' +
        '<circle cx="14" cy="14" r="6" fill="white"/>' +
      '</svg>';
    return window.L.divIcon({
      html: svg,
      className: '',
      iconSize: [28, 36],
      iconAnchor: [14, 36],
      popupAnchor: [0, -38]
    });
  }

  function initMap(lat, lng) {
    if (!window.L) return;

    if (!leafletMap) {
      leafletMap = window.L.map('lf-map', { zoomControl: true, scrollWheelZoom: true });
      window.L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        { attribution: '© OpenStreetMap © CartoDB', maxZoom: 18 }
      ).addTo(leafletMap);
    }

    leafletMap.setView([lat, lng], 10);
  }

  function updateMapMarkers(clinics) {
    if (!leafletMap) return;

    var newIds = {};
    clinics.forEach(function (c) { newIds[c.id] = true; });

    var changed = false;

    // Remove stale markers
    Object.keys(markers).forEach(function (id) {
      if (!newIds[id]) {
        leafletMap.removeLayer(markers[id]);
        delete markers[id];
        changed = true;
      }
    });

    // Add new markers
    clinics.forEach(function (c) {
      if (markers[c.id]) return;

      var m = window.L.marker([c.lat, c.lng], { icon: makeIcon(false) })
        .addTo(leafletMap)
        .bindPopup(
          '<p class="lf-popup__name">' + escHtml(cleanTitle(c.title)) + '</p>' +
          '<p class="lf-popup__addr">' + escHtml(c.address || '') + '</p>' +
          (c.url
            ? '<a class="lf-popup__link" href="' + escHtml(c.url) + '">View clinic &rarr;</a>'
            : '')
        );

      m.on('click', function () {
        var card = document.querySelector('.lf-card[data-id="' + c.id + '"]');
        if (card) {
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          card.classList.add('lf-card--highlighted');
          setTimeout(function () { card.classList.remove('lf-card--highlighted'); }, 1800);
        }
      });

      markers[c.id] = m;
      changed = true;
    });

    // Only fit bounds when the marker set actually changed (e.g. initial load or filter change).
    // Repeated searches with the same postcode leave markers unchanged so we skip fitBounds,
    // letting initMap's setView keep the map centred on the searched location.
    if (changed && clinics.length > 0) {
      var bounds = window.L.latLngBounds(clinics.map(function (c) { return [c.lat, c.lng]; }));
      leafletMap.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }
  }

  function highlightPin(id, on) {
    if (markers[id]) markers[id].setIcon(makeIcon(on));
  }

  function panToClinic(clinic) {
    if (!leafletMap) return;
    leafletMap.setView([clinic.lat, clinic.lng], 14);
    if (markers[clinic.id]) markers[clinic.id].openPopup();
  }

  /* ── SHOW RESULTS PANEL ── */
  function showResultsPanel() {
    var empty   = document.getElementById('lf-empty');
    var results = document.getElementById('lf-results');
    if (empty)   empty.style.display = 'none';
    if (results) results.classList.add('lf-visible');
  }

  /* ── RUN SEARCH ── */
  function runSearch(lat, lng, label) {
    userLat = lat;
    userLng = lng;
    currentLabel = label;
    visibleCount = PAGE_SIZE;

    var clinics = window.GBClinics || [];

    sortedClinics = clinics
      .map(function (c) {
        return Object.assign({}, c, { _dist: calcDistance(lat, lng, c.lat, c.lng) });
      })
      .sort(function (a, b) { return a._dist - b._dist; });

    showResultsPanel();
    setSortLabel('Sorted by distance');

    // Leaflet may not be ready if deferred — wait for it
    if (window.L) {
      initMap(lat, lng);
    } else {
      document.querySelector('script[src*="leaflet"]').addEventListener('load', function () {
        initMap(lat, lng);
      });
    }

    renderResults();
  }

  function setSortLabel(text) {
    var el = document.getElementById('lf-sort-label');
    if (el) el.textContent = text;
  }

  /* ── DEFAULT UK-WIDE VIEW (no search required) ── */
  function showAllClinics() {
    var clinics = window.GBClinics || [];
    if (!clinics.length) return;

    sortedClinics = clinics.slice().sort(function (a, b) {
      return (a.title || '').localeCompare(b.title || '');
    });

    currentLabel = '';
    visibleCount = PAGE_SIZE;

    showResultsPanel();

    function doInit() {
      // Centre on UK, let fitBounds override once markers are placed
      if (!leafletMap) {
        leafletMap = window.L.map('lf-map', { zoomControl: true, scrollWheelZoom: true });
        window.L.tileLayer(
          'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
          { attribution: '© OpenStreetMap © CartoDB', maxZoom: 18 }
        ).addTo(leafletMap);
        leafletMap.setView([54.5, -3.0], 6);
      }
      renderResults();
    }

    if (window.L) {
      doInit();
    } else {
      document.querySelector('script[src*="leaflet"]').addEventListener('load', doInit);
    }
  }

  /* ── POSTCODES.IO GEOCODE (free, no key) ── */
  function geocodePostcode(raw, callback) {
    var pc = raw.trim().toUpperCase().replace(/\s+/g, '');

    fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(pc))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status === 200) {
          callback(null, { lat: data.result.latitude, lng: data.result.longitude, label: raw.trim() });
          return;
        }
        // Try outward code (e.g. "AB10")
        var outward = pc.replace(/\d[A-Z]{2}$/, '');
        return fetch('https://api.postcodes.io/outcodes/' + encodeURIComponent(outward))
          .then(function (r2) { return r2.json(); })
          .then(function (d2) {
            if (d2.status === 200) {
              callback(null, { lat: d2.result.latitude, lng: d2.result.longitude, label: raw.trim() });
            } else {
              callback(new Error('Postcode not found. Please check and try again.'));
            }
          });
      })
      .catch(function () {
        callback(new Error('Could not reach postcode service. Please try again.'));
      });
  }

  /* ── UI WIRING ── */
  function showError(msg) {
    var el = document.getElementById('lf-search-error');
    if (!el) return;
    el.textContent = msg;
    el.style.display = msg ? 'block' : 'none';
  }

  function setSearchLoading(loading) {
    var btn = document.getElementById('lf-search-btn');
    if (!btn) return;
    btn.textContent = loading ? 'Searching…' : (btn.dataset.label || 'Search');
    btn.classList.toggle('lf-loading', loading);
  }

  onReady(function () {
    var postcodeInput = document.getElementById('lf-postcode');
    var searchBtn     = document.getElementById('lf-search-btn');
    var geoBtn        = document.getElementById('lf-geo-btn');
    var moreBtn       = document.getElementById('lf-more-btn');
    var mapToggle     = document.getElementById('lf-map-toggle');
    var listBtn       = document.getElementById('lf-list-btn');
    var resultsEl     = document.getElementById('lf-results');

    if (!searchBtn) return; // section not on page

    // Store original label
    searchBtn.dataset.label = searchBtn.textContent;

    // Search on button click
    searchBtn.addEventListener('click', function () {
      var val = postcodeInput.value.trim();
      showError('');
      if (!val) { showError('Please enter a postcode.'); return; }

      setSearchLoading(true);
      geocodePostcode(val, function (err, result) {
        setSearchLoading(false);
        if (err) { showError(err.message); return; }
        runSearch(result.lat, result.lng, result.label);
      });
    });

    // Search on Enter key
    postcodeInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') searchBtn.click();
    });

    // Geolocation
    geoBtn.addEventListener('click', function () {
      showError('');
      if (!navigator.geolocation) {
        showError('Geolocation is not supported by your browser.');
        return;
      }

      var original = geoBtn.textContent;
      geoBtn.textContent = '📍 Locating…';

      navigator.geolocation.getCurrentPosition(
        function (pos) {
          geoBtn.textContent = original;
          runSearch(pos.coords.latitude, pos.coords.longitude, 'your location');
        },
        function () {
          geoBtn.textContent = original;
          showError('Could not access your location. Please enter a postcode instead.');
        }
      );
    });

    // Filter chips
    document.querySelectorAll('.lf-chip').forEach(function (chip) {
      chip.addEventListener('click', function () {
        document.querySelectorAll('.lf-chip').forEach(function (c) {
          c.classList.remove('lf-chip--active');
        });
        chip.classList.add('lf-chip--active');
        currentFilter = chip.dataset.filter;
        visibleCount = PAGE_SIZE;
        renderResults();
      });
    });

    // Show more
    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        visibleCount += PAGE_SIZE;
        renderResults();
      });
    }

    // Mobile map toggle
    function showMap() {
      resultsEl.classList.add('lf-map-active');
      if (listBtn) listBtn.classList.add('lf-list-btn--visible');
      if (mapToggle) mapToggle.setAttribute('aria-expanded', 'true');
      if (leafletMap) setTimeout(function () { leafletMap.invalidateSize(); }, 50);
    }

    function showList() {
      resultsEl.classList.remove('lf-map-active');
      if (listBtn) listBtn.classList.remove('lf-list-btn--visible');
      if (mapToggle) mapToggle.setAttribute('aria-expanded', 'false');
    }

    if (mapToggle) {
      mapToggle.addEventListener('click', function () {
        resultsEl.classList.contains('lf-map-active') ? showList() : showMap();
      });
    }

    if (listBtn) {
      listBtn.addEventListener('click', showList);
    }

    // Show all clinics on load — no search required
    showAllClinics();

  }); // end onReady

}());
